/**
 * SEO Helper Module
 * Production-ready utilities for SEO optimization
 */

const SITE_CONFIG = {
  name: 'DailyTechTrend',
  siteName: 'dailytechtrend',
  url: process.env.REACT_APP_SITE_URL || 'https://dailytechtrend.com',
  description: 'Discover the cutting-edge AI developments transforming industries in 2025from generative models and AI agents to ethical advances and automation. Get expert insights, practical guides, and up-to-date analysis on the hottest artificial intelligence trends.',
  shortDescription: 'Latest AI trends, insights, and analysis for 2025. Expert guides on machine learning, generative AI, and emerging technologies.',
  twitterHandle: '@dailytechtrend',
  image: '/logo.png',
  favicon: '/favicon.ico',
  author: 'AI Research Team',
  keywords: 'AI trends, artificial intelligence, machine learning, generative AI, deep learning, technology news, AI research, automation, data science',
  language: 'en',
  locale: 'en_US',
  themeColor: '#3b82f6'
};

/**
 * Generate meta tags for a page
 * @param {Object} options - SEO options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.keywords - Keywords (comma-separated)
 * @param {string} options.image - Image URL
 * @param {string} options.url - Canonical URL
 * @param {string} options.type - Open Graph type (article, website)
 * @param {Date} options.publishedTime - Publication date
 * @param {Date} options.modifiedTime - Last modified date
 * @param {string} options.author - Author name
 * @param {string} options.section - Article section/category
 * @param {Array} options.tags - Article tags
 * @returns {Object} Meta tags object for react-helmet-async
 */
export const generateMetaTags = (options = {}) => {
  const {
    title,
    description = SITE_CONFIG.description,
    keywords,
    image = SITE_CONFIG.image,
    url,
    type = 'website',
    publishedTime,
    modifiedTime,
    author = SITE_CONFIG.author,
    section,
    tags = []
  } = options;

  // Ensure proper title formatting without truncation
  const cleanTitle = title ? title.replace(/\.\.\.$/,'').trim() : '';
  const fullTitle = cleanTitle ? `${cleanTitle} | ${SITE_CONFIG.name}` : SITE_CONFIG.name;
  
  // Optimize description length (max 155 characters)
  let optimizedDescription = description || SITE_CONFIG.description;
  if (optimizedDescription.length > 155) {
    const truncated = optimizedDescription.substring(0, 155);
    const lastSpace = truncated.lastIndexOf(' ');
    optimizedDescription = (lastSpace > 125 ? truncated.substring(0, lastSpace) : truncated).trim() + '...';
  }
  
  // Fix URL concatenation to prevent duplicate domains
  let fullUrl;
  if (url) {
    if (url.startsWith('http')) {
      fullUrl = url; // Already a full URL
    } else {
      fullUrl = url.startsWith('/') ? `${SITE_CONFIG.url}${url}` : `${SITE_CONFIG.url}/${url}`;
    }
  } else {
    fullUrl = SITE_CONFIG.url;
  }
  
  const fullImage = image?.startsWith('http') ? image : `${SITE_CONFIG.url}${image || '/logo.png'}`;

  return {
    title: fullTitle,
    meta: [
      // Essential meta tags
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
      { name: 'theme-color', content: SITE_CONFIG.themeColor },
      { name: 'description', content: optimizedDescription },
      { name: 'keywords', content: keywords || SITE_CONFIG.keywords },
      { name: 'author', content: author },
      { name: 'robots', content: type === 'article' ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' : 'index, follow, max-image-preview:large' },
      { name: 'language', content: SITE_CONFIG.language },
      { name: 'revisit-after', content: '7 days' },
      { name: 'format-detection', content: 'telephone=no' },
      
      // Additional SEO meta tags
      { name: 'distribution', content: 'global' },
      { name: 'rating', content: 'general' },
      { name: 'classification', content: 'Artificial Intelligence, Technology' },
      ...(type === 'article' ? [
        { name: 'article:author:name', content: author },
        { name: 'article:section', content: section || 'AI Technology' },
        { name: 'article:published_time', content: publishedTime ? publishedTime.toISOString() : new Date().toISOString() },
        { name: 'article:modified_time', content: modifiedTime ? modifiedTime.toISOString() : new Date().toISOString() }
      ] : []),
      
      // Security and performance
      { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
      { name: 'referrer', content: 'origin-when-cross-origin' },
      
      // Open Graph
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: optimizedDescription },
      { property: 'og:image', content: fullImage },
      { property: 'og:image:alt', content: `${cleanTitle || SITE_CONFIG.name} - AI insights and trends` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:url', content: fullUrl },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: SITE_CONFIG.name },
      { property: 'og:locale', content: SITE_CONFIG.locale },
      
      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: optimizedDescription },
      { name: 'twitter:image', content: fullImage },
      { name: 'twitter:image:alt', content: `${cleanTitle || SITE_CONFIG.name} - AI insights` },
      { name: 'twitter:site', content: SITE_CONFIG.twitterHandle },
      { name: 'twitter:creator', content: SITE_CONFIG.twitterHandle },
      { name: 'twitter:domain', content: 'dailytechtrend.com' },
      
      // Article specific (only if type is article)
      ...(type === 'article' ? [
        { property: 'article:author', content: author },
        { property: 'article:section', content: section || 'AI Technology' },
        { property: 'article:publisher', content: SITE_CONFIG.url },
        { property: 'article:tag', content: 'Artificial Intelligence' },
        ...(publishedTime ? [{ property: 'article:published_time', content: publishedTime.toISOString() }] : []),
        ...(modifiedTime ? [{ property: 'article:modified_time', content: modifiedTime.toISOString() }] : []),
        ...tags.map(tag => ({ property: 'article:tag', content: tag }))
      ] : [])
    ],
    link: [
      // Canonical URL (most important - prevent duplicate content)
      { rel: 'canonical', href: fullUrl },
      
      // Favicon and app icons
      { rel: 'icon', href: '/favicon.ico' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/site.webmanifest' },
      
      // DNS prefetch and preconnect for performance
      { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      { rel: 'dns-prefetch', href: 'https://storage.googleapis.com' },
      
      // Alternate language versions (if you have them)
      { rel: 'alternate', hrefLang: 'en', href: fullUrl },
      { rel: 'alternate', hrefLang: 'x-default', href: fullUrl }
    ]
  };
};

/**
 * Generate JSON-LD structured data for a blog post
 * @param {Object} post - Blog post data
 * @param {string} post.id - Post ID
 * @param {string} post.title - Post title
 * @param {string} post.content - Post content
 * @param {string} post.excerpt - Post excerpt
 * @param {string} post.image - Post image URL
 * @param {string} post.author - Post author
 * @param {Date} post.date - Publication date
 * @param {string} post.category - Post category
 * @param {Array} post.tags - Post tags
 * @param {string} post.url - Post URL
 * @returns {Object} JSON-LD script tag
 */
export const generateBlogPostSchema = (post) => {
  const {
    id,
    title,
    content,
    excerpt,
    image,
    author = SITE_CONFIG.author,
    date,
    category,
    tags = [],
    url
  } = post;

  // Fix URL generation for blog posts
  let fullUrl;
  if (url) {
    if (url.startsWith('http')) {
      fullUrl = url;
    } else {
      fullUrl = url.startsWith('/') ? `${SITE_CONFIG.url}${url}` : `${SITE_CONFIG.url}/${url}`;
    }
  } else {
    fullUrl = `${SITE_CONFIG.url}/blog/${id}`;
  }
  
  const fullImage = image?.startsWith('http') ? image : `${SITE_CONFIG.url}${image || SITE_CONFIG.image}`;

  const wordCount = content ? content.split(/\s+/).filter(word => word.length > 0).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200)); // 200 words per minute
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    'headline': title,
    'alternativeHeadline': excerpt || content?.substring(0, 160),
    'description': excerpt || content?.substring(0, 160),
    'image': {
      '@type': 'ImageObject',
      'url': fullImage,
      'width': 1200,
      'height': 630
    },
    'author': {
      '@type': 'Person',
      'name': author,
      'url': `${SITE_CONFIG.url}/about`
    },
    'publisher': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.url,
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_CONFIG.url}/logo.png`,
        'width': 512,
        'height': 512
      }
    },
    'datePublished': date ? new Date(date).toISOString() : new Date().toISOString(),
    'dateModified': date ? new Date(date).toISOString() : new Date().toISOString(),
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    'url': fullUrl,
    'articleSection': category || 'AI Technology',
    'about': {
      '@type': 'Thing',
      'name': category || 'Artificial Intelligence'
    },
    'keywords': tags.length > 0 ? tags.join(', ') : 'AI, artificial intelligence, technology',
    'wordCount': wordCount,
    'timeRequired': `PT${readTime}M`,
    'isAccessibleForFree': true,
    'inLanguage': 'en-US',
    'copyrightHolder': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.url
    },
    'copyrightYear': new Date().getFullYear(),
    'discussionUrl': fullUrl,
    'potentialAction': {
      '@type': 'ReadAction',
      'target': fullUrl
    }
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Generate FAQ schema for posts with FAQ sections
 * @param {Array} faqs - Array of FAQ objects
 * @param {string} faqs[].question - FAQ question
 * @param {string} faqs[].answer - FAQ answer
 * @returns {Object} JSON-LD script tag
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Generate Organization schema for the site
 * @returns {Object} JSON-LD script tag
 */
export const generateOrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': SITE_CONFIG.name,
    'url': SITE_CONFIG.url,
    'logo': `${SITE_CONFIG.url}/logo.png`,
    'description': SITE_CONFIG.description,
    'sameAs': [
      `https://twitter.com/${SITE_CONFIG.twitterHandle.replace('@', '')}`,
      // Add other social media profiles if available
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'url': `${SITE_CONFIG.url}/contact`
    },
    'publishingPrinciples': `${SITE_CONFIG.url}/about`,
    'knowsAbout': [
      'Artificial Intelligence',
      'Machine Learning',
      'Deep Learning',
      'Generative AI',
      'Computer Vision',
      'Natural Language Processing',
      'AI Research',
      'Technology'
    ]
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Generate Breadcrumb schema for navigation
 * @param {Array} breadcrumbs - Array of breadcrumb objects
 * @returns {Object} JSON-LD script tag
 */
export const generateBreadcrumbSchema = (breadcrumbs = []) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': crumb.name,
      'item': `${SITE_CONFIG.url}${crumb.url}`
    }))
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Generate sitemap XML content
 * @param {Array} posts - Array of blog posts
 * @param {Array} staticPages - Array of static pages
 * @returns {string} XML sitemap content
 */
export const generateSitemapXML = (posts = [], staticPages = []) => {
  const baseUrl = SITE_CONFIG.url;

  // Static pages
  const staticEntries = [
    { url: '/', priority: '1.0', changefreq: 'monthly' },
    ...staticPages.map(page => ({
      url: page.url,
      priority: page.priority || '0.8',
      changefreq: page.changefreq || 'monthly',
      lastmod: page.lastmod
    }))
  ];

  // Blog posts
  const postEntries = posts.map(post => ({
    url: `/blog/${post.id}`,
    priority: '0.8',
    changefreq: 'weekly',
    lastmod: post.date || post.metadata?.date
  }));

  const allEntries = [...staticEntries, ...postEntries];

  const urlEntries = allEntries.map(entry => {
    const lastmod = entry.lastmod ?
      new Date(entry.lastmod).toISOString().split('T')[0] :
      new Date().toISOString().split('T')[0];

    return `  <url>
    <loc>${baseUrl}${entry.url}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>${entry.changefreq}</changefreq>
    <priority>${entry.priority}</priority>
  </url>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;
};

/**
 * Generate robots.txt content
 * @param {string} sitemapUrl - Full URL to sitemap
 * @returns {string} robots.txt content
 */
export const generateRobotsTxt = (sitemapUrl) => {
  return `User-agent: *
Disallow: /admin/
Disallow: /api/
Disallow: /dashboard/

Sitemap: ${sitemapUrl || `https://dailytechtrend.com/sitemap.xml`}`;
};

/**
 * Ping search engines with updated sitemap
 * @param {string} sitemapUrl - Full sitemap URL
 * @returns {Promise<Array>} Array of ping results
 */
export const pingSearchEngines = async (sitemapUrl) => {
  const engines = [
    `https://www.google.com/ping?sitemap=${encodeURIComponent(sitemapUrl)}`,
    `https://www.bing.com/ping?sitemap=${encodeURIComponent(sitemapUrl)}`
  ];

  const results = [];

  for (const url of engines) {
    try {
      const response = await fetch(url);
      results.push({
        engine: url.includes('google') ? 'Google' : 'Bing',
        success: response.ok,
        status: response.status
      });
    } catch (error) {
      results.push({
        engine: url.includes('google') ? 'Google' : 'Bing',
        success: false,
        error: error.message
      });
    }
  }

  return results;
};

/**
 * Extract FAQ items from post content (if it contains FAQ sections)
 * @param {string} content - Post content
 * @returns {Array} Array of FAQ objects
 */
export const extractFAQs = (content) => {
  if (!content) return [];

  // Simple regex to find FAQ patterns
  const faqPattern = /FAQ|Q&A|Question:|Answer:/gi;
  if (!faqPattern.test(content)) return [];

  // This is a basic implementation - you might want to enhance this
  // based on your actual content structure
  const faqs = [];
  const lines = content.split('\n');

  let currentQuestion = '';
  let currentAnswer = '';

  for (const line of lines) {
    if (line.toLowerCase().includes('question') || line.match(/^\d+\./)) {
      if (currentQuestion && currentAnswer) {
        faqs.push({ question: currentQuestion, answer: currentAnswer });
      }
      currentQuestion = line.replace(/^(question:\s*|q:\s*|\d+\.\s*)/i, '').trim();
      currentAnswer = '';
    } else if (line.toLowerCase().includes('answer') && currentQuestion) {
      currentAnswer = line.replace(/^(answer:\s*|a:\s*)/i, '').trim();
    } else if (currentQuestion && line.trim()) {
      currentAnswer += (currentAnswer ? ' ' : '') + line.trim();
    }
  }

  if (currentQuestion && currentAnswer) {
    faqs.push({ question: currentQuestion, answer: currentAnswer });
  }

  return faqs;
};

/**
 * Generate SEO-friendly URL slug from title
 * @param {string} title - Post title
 * @returns {string} URL slug
 */
export const generateSlug = (title) => {
  if (!title) return 'untitled-post';

  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 80); // Limit length
};

/**
 * Generate SEO-friendly URL slug from title with ID fallback
 * @param {string} title - Post title
 * @param {string} id - Post ID (fallback)
 * @returns {string} URL slug
 */
export const generateSEOSlug = (title, id) => {
  const slug = generateSlug(title);
  return slug || `post-${id}`;
};

/**
 * Calculate estimated read time for content
 * @param {string} content - Text content
 * @param {number} wordsPerMinute - Reading speed (default: 200)
 * @returns {string} Formatted read time
 */
export const calculateReadTime = (content, wordsPerMinute = 200) => {
  if (!content) return '1 min read';

  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);

  if (minutes < 1) return '1 min read';
  if (minutes === 1) return '1 min read';
  return `${minutes} min read`;
};

/**
 * Validate SEO metadata
 * @param {Object} meta - Meta object to validate
 * @returns {Object} Validation results
 */
export const validateSEO = (meta) => {
  const issues = [];

  if (!meta.title || meta.title.length < 30) {
    issues.push('Title should be 30-60 characters');
  }

  if (!meta.description || meta.description.length < 120) {
    issues.push('Description should be 120-160 characters');
  }

  if (!meta.image) {
    issues.push('Open Graph image is required');
  }

  return {
    valid: issues.length === 0,
    issues
  };
};

/**
 * Generate website schema (for homepage)
 * @returns {Object} JSON-LD script tag
 */
export const generateWebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': SITE_CONFIG.name,
    'alternateName': SITE_CONFIG.siteName,
    'url': SITE_CONFIG.url,
    'description': SITE_CONFIG.description,
    'inLanguage': SITE_CONFIG.language,
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${SITE_CONFIG.url}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    'publisher': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.url,
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_CONFIG.url}/logo.png`
      }
    },
    'mainEntity': {
      '@type': 'Blog',
      'name': `${SITE_CONFIG.name} Blog`,
      'description': 'Latest insights and trends in artificial intelligence, machine learning, and emerging technologies.',
      'url': `${SITE_CONFIG.url}/blog`,
      'publisher': {
        '@type': 'Organization',
        'name': SITE_CONFIG.name
      }
    }
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Generate Blog schema for blog listing pages
 * @param {Array} posts - Recent blog posts
 * @returns {Object} JSON-LD script tag
 */
export const generateBlogSchema = (posts = []) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': `${SITE_CONFIG.name} - AI Insights Blog`,
    'description': 'Latest insights, trends, and analysis in artificial intelligence, machine learning, and emerging technologies.',
    'url': `${SITE_CONFIG.url}/blog`,
    'inLanguage': SITE_CONFIG.language,
    'publisher': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.url,
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_CONFIG.url}/logo.png`
      }
    },
    'blogPost': posts.slice(0, 10).map(post => ({
      '@type': 'BlogPosting',
      'headline': post.title,
      'url': `${SITE_CONFIG.url}/blog/${post.id}`,
      'datePublished': post.date ? new Date(post.date).toISOString() : new Date().toISOString(),
      'author': {
        '@type': 'Person',
        'name': post.author || SITE_CONFIG.author
      }
    }))
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema, null, 2)
  };
};

/**
 * Clean and optimize title for SEO
 * @param {string} title - Original title
 * @param {number} maxLength - Maximum length (default 60)
 * @returns {string} Optimized title
 */
export const optimizeTitle = (title, maxLength = 60) => {
  if (!title) return SITE_CONFIG.name;
  
  // Remove truncation indicators
  let cleanTitle = title.replace(/\.{3,}$/g, '').trim();
  
  // If title is too long, truncate smartly
  if (cleanTitle.length > maxLength - SITE_CONFIG.name.length - 3) {
    const availableLength = maxLength - SITE_CONFIG.name.length - 3; // 3 for " | "
    cleanTitle = cleanTitle.substring(0, availableLength).trim();
    
    // Don't cut in the middle of a word
    if (cleanTitle.length < title.length && cleanTitle.lastIndexOf(' ') > availableLength * 0.8) {
      cleanTitle = cleanTitle.substring(0, cleanTitle.lastIndexOf(' '));
    }
  }
  
  return cleanTitle;
};

/**
 * Generate optimized meta description
 * @param {string} content - Post content or excerpt
 * @param {number} maxLength - Maximum length (default 155)
 * @returns {string} Optimized description
 */
export const optimizeDescription = (content, maxLength = 155) => {
  if (!content) return SITE_CONFIG.shortDescription;
  
  // Clean content (remove HTML, extra spaces, placeholders)
  let cleanContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/\[.*?Placeholder.*?\]/gi, '') // Remove placeholder text
    .replace(/\s+/g, ' ')
    .trim();
  
  if (cleanContent.length <= maxLength) return cleanContent;
  
  // Truncate at word boundary
  let truncated = cleanContent.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.8) {
    truncated = truncated.substring(0, lastSpace);
  }
  
  // Add ellipsis if truncated
  return truncated.trim() + (truncated.length < cleanContent.length ? '...' : '');
};

/**
 * Validate and fix URL structure
 * @param {string} url - URL to validate
 * @returns {string} Fixed URL
 */
export const validateAndFixUrl = (url) => {
  if (!url) return '';
  
  // Remove duplicate domain issues
  if (url.includes(SITE_CONFIG.url)) {
    const parts = url.split(SITE_CONFIG.url);
    if (parts.length > 2) {
      // Multiple instances of the domain
      return parts[parts.length - 1].startsWith('/') ? parts[parts.length - 1] : `/${parts[parts.length - 1]}`;
    }
  }
  
  // Ensure proper path format
  if (url.startsWith('http')) return url;
  return url.startsWith('/') ? url : `/${url}`;
};

/**
 * Generate comprehensive meta tags for blog posts with all SEO best practices
 * @param {Object} post - Blog post data
 * @returns {Object} Complete meta tags for react-helmet-async
 */
export const generateBlogPostMeta = (post) => {
  const {
    id,
    title,
    content,
    excerpt,
    image,
    author = SITE_CONFIG.author,
    date,
    category,
    tags = [],
    url
  } = post;

  // Optimize title and description
  const optimizedTitle = optimizeTitle(title);
  const optimizedDescription = optimizeDescription(excerpt || content);
  
  // Fix URL issues
  const cleanUrl = validateAndFixUrl(url || `/blog/${id}`);
  
  // Generate proper tags
  const postTags = tags.length > 0 ? tags : ['AI', 'Technology', 'Innovation'];
  const keywords = postTags.join(', ');
  
  return generateMetaTags({
    title: optimizedTitle,
    description: optimizedDescription,
    keywords,
    image,
    url: cleanUrl,
    type: 'article',
    publishedTime: date ? new Date(date) : new Date(),
    modifiedTime: date ? new Date(date) : new Date(),
    author,
    section: category || 'AI Technology',
    tags: postTags
  });
};

export { SITE_CONFIG };